import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, X, ScanLine, Phone, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useCountUp } from "../lib/useCountUp";
import { PHONE_DISPLAY, PHONE_TEL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScanTool = () => {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | scanning | results
  const [checks, setChecks] = useState([]);
  const [visible, setVisible] = useState(0);
  const [score, setScore] = useState(0);
  const [scanUrl, setScanUrl] = useState("");
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const resultRef = useRef(null);
  const shownScore = useCountUp(score, 1400, visible >= checks.length && checks.length > 0);

  const startScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setPhase("scanning");
    setError("");
    setChecks([]);
    setVisible(0);
    setDone(false);
    try {
      const { data } = await axios.post(`${API}/scan`, { url: url.trim() }, { timeout: 25000 });
      if (!data.ok) {
        setError(data.error || "We couldn't scan that site.");
        setPhase("idle");
        return;
      }
      setChecks(data.checks);
      setScore(data.score);
      setScanUrl(data.url);
      setPhase("results");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong reaching the scanner. Please try again.");
      setPhase("idle");
    }
  };

  useEffect(() => {
    if (phase !== "results" || visible >= checks.length) return;
    const t = setTimeout(() => setVisible((v) => v + 1), 380);
    return () => clearTimeout(t);
  }, [phase, visible, checks.length]);

  useEffect(() => {
    if (phase === "results") resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [phase]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/leads`,
        { name, email, phone, website: scanUrl, message: `Free ADA scan request — pre-check score ${score}/100` },
        { timeout: 20000 }
      );
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="scan" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_50%_0%,rgba(0,229,255,0.07),transparent_60%)]" />
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="text-center">
          <Eyebrow className="text-center">FREE INSTANT SCAN — RUNS RIGHT NOW</Eyebrow>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">
            IS YOUR SITE ONE OF
            <br />
            <span className="text-alert">THE 94.8%?</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg text-slate-300">
            Type your address and watch the pre-check run live. You'll get a score in seconds — then we email your full
            report and the fixes.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-panel shadow-2xl shadow-black/50" data-testid="scan-tool-card">
            <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3.5">
              <span className="h-3 w-3 rounded-full bg-alert/80" />
              <span className="h-3 w-3 rounded-full bg-brandamber/80" />
              <span className="h-3 w-3 rounded-full bg-safe/80" />
              <span className="ml-3 font-mono text-[11px] tracking-[0.2em] text-slate-500">wecareada-precheck v1.0</span>
            </div>

            <div className="p-5 sm:p-8">
              <form onSubmit={startScan} className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-mono text-sm text-cy">
                    https://
                  </span>
                  <input
                    data-testid="scan-url-input"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="yoursite.com"
                    className="w-full rounded-xl border border-white/15 bg-ink py-4 pl-[4.9rem] pr-4 font-mono text-sm text-white placeholder:text-slate-600 focus:border-cy focus:outline-none focus:ring-2 focus:ring-cy/30"
                  />
                </div>
                <button
                  data-testid="scan-start-button"
                  type="submit"
                  disabled={phase === "scanning" || !url.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-brandamber px-7 py-4 font-bold text-ink transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                >
                  {phase === "scanning" ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Scanning…
                    </>
                  ) : (
                    <>
                      <ScanLine className="h-5 w-5" /> Scan My Site — FREE
                    </>
                  )}
                </button>
              </form>

              {error && (
                <p data-testid="scan-error-message" className="mt-4 flex items-start gap-2 rounded-xl border border-alert/40 bg-alert/10 p-4 text-sm text-red-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alert" /> {error}
                </p>
              )}

              {phase === "results" && (
                <div ref={resultRef} className="mt-6" data-testid="scan-results-panel">
                  <div className="space-y-2 font-mono text-xs sm:text-sm">
                    {checks.slice(0, visible).map((c, i) => (
                      <motion.div
                        key={c.id}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.35 }}
                        data-testid={`scan-check-item-${c.id}`}
                        className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
                          c.pass ? "border-safe/25 bg-safe/5" : "border-alert/35 bg-alert/10"
                        }`}
                      >
                        {c.pass ? (
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-safe" />
                        ) : (
                          <X className="mt-0.5 h-4 w-4 shrink-0 text-alert" />
                        )}
                        <div>
                          <span className={c.pass ? "text-slate-300" : "text-white font-semibold"}>{c.label}</span>
                          <span className="ml-2 text-slate-500">— {c.detail}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {visible >= checks.length && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="mt-6"
                    >
                      <div className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-ink/70 p-6 sm:flex-row sm:justify-between">
                        <div className="text-center sm:text-left">
                          <p className="font-mono text-[11px] tracking-[0.22em] text-slate-500">PRE-CHECK SCORE</p>
                          <p className="font-display text-6xl leading-none mt-1" data-testid="scan-score-value">
                            <span className={score < 70 ? "text-alert" : score < 90 ? "text-brandamber" : "text-safe"}>
                              {shownScore}
                            </span>
                            <span className="text-2xl text-slate-600">/100</span>
                          </p>
                          <p className="mt-1 font-mono text-[10px] text-slate-600 truncate max-w-[16rem] sm:max-w-none">{scanUrl}</p>
                        </div>
                        <p className="max-w-xs text-center text-sm leading-relaxed text-slate-400 sm:text-left">
                          This is the 10-second pre-check. Your <span className="text-white">full report</span> — every
                          page, every error, with code fixes — is scored against WCAG 2.2 and emailed to you.
                        </p>
                      </div>

                      {!done ? (
                        <form onSubmit={submitLead} className="mt-6 rounded-2xl border border-brandamber/30 bg-brandamber/5 p-5 sm:p-6" data-testid="lead-form-card">
                          <p className="font-semibold text-white">Where should we email your full report?</p>
                          <div className="mt-4 grid gap-3 sm:grid-cols-3">
                            <input
                              data-testid="lead-name-input"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="Your name *"
                              required
                              className="rounded-xl border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-600 focus:border-brandamber focus:outline-none"
                            />
                            <input
                              data-testid="lead-email-input"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="Email address *"
                              type="email"
                              required
                              className="rounded-xl border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-600 focus:border-brandamber focus:outline-none"
                            />
                            <input
                              data-testid="lead-phone-input"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="Phone (optional)"
                              type="tel"
                              className="rounded-xl border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-600 focus:border-brandamber focus:outline-none"
                            />
                          </div>
                          <button
                            data-testid="lead-submit-button"
                            type="submit"
                            disabled={sending}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandamber px-6 py-4 font-bold text-ink transition-all hover:scale-[1.01] active:scale-95 disabled:opacity-60"
                          >
                            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ChevronRight className="h-5 w-5" />}
                            Email Me My Full Report — FREE
                          </button>
                          <p className="mt-3 text-center font-mono text-[10px] tracking-wider text-slate-500">
                            NO SPAM. YOUR REPORT GOES STRAIGHT TO EDDIE'S INBOX.
                          </p>
                        </form>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.96 }}
                          animate={{ opacity: 1, scale: 1 }}
                          data-testid="scan-success-panel"
                          className="mt-6 rounded-2xl border border-safe/40 bg-safe/10 p-6 text-center sm:p-8"
                        >
                          <p className="font-display text-3xl text-white">YOU'RE IN, {name.split(" ")[0].toUpperCase()}.</p>
                          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-300">
                            Your full report is on its way to <span className="text-safe">{email}</span>. Eddie will
                            follow up within one business day — or skip the wait:
                          </p>
                          <a
                            data-testid="success-call-button"
                            href={PHONE_TEL}
                            className="mt-5 inline-flex items-center gap-2 rounded-full border border-cy/50 bg-cy/10 px-6 py-3 font-semibold text-cy transition-all hover:bg-cy/20"
                          >
                            <Phone className="h-4 w-4" /> Call Eddie now — {PHONE_DISPLAY}
                          </a>
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          </div>
          <p className="mt-4 text-center font-mono text-[10px] tracking-wider text-slate-600">
            THE PRE-CHECK RUNS LIVE ON THIS PAGE. YOUR FULL AXE-CORE WCAG 2.2 A/AA SCAN (UP TO 25 PAGES) ARRIVES BY EMAIL.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default ScanTool;
